import type { FeatureVelocity, Benchmark } from "./types.js";

/**
 * Generate benchmarks by complexity + category from feature velocities.
 */
export function generateBenchmarks(
  features: FeatureVelocity[],
): Benchmark[] {
  // Only features with actual data
  const withData = features.filter((f) => f.sessionCount > 0);
  if (withData.length === 0) return [];

  // Group by complexity + category
  const groups = new Map<string, FeatureVelocity[]>();
  for (const f of withData) {
    const key = `${f.complexity}-${f.category}`;
    const group = groups.get(key) ?? [];
    group.push(f);
    groups.set(key, group);
  }

  const benchmarks: Benchmark[] = [];

  for (const [key, group] of groups) {
    if (group.length === 0) continue;

    const complexity = group[0].complexity;
    const category = group[0].category;

    // Averages
    const avgTokens = mean(group.map((f) => f.totalTokens));
    const avgCost = mean(group.map((f) => f.totalCost));
    const avgSessions = mean(group.map((f) => f.sessionCount));
    const avgWallClockDays = mean(group.map((f) => f.wallClockDays));
    const avgActiveHours = mean(group.map((f) => f.activeHours));

    // Percentile ranges
    const tokenRange = percentileRange(group.map((f) => f.totalTokens));
    const costRange = percentileRange(group.map((f) => f.totalCost));
    const sessionRange = percentileRange(group.map((f) => f.sessionCount));

    benchmarks.push({
      key,
      complexity,
      category,
      dataPoints: group.length,
      avgTokens,
      avgCost,
      avgSessions,
      avgWallClockDays,
      avgActiveHours,
      tokenRange,
      costRange,
      sessionRange,
    });
  }

  return benchmarks.sort((a, b) => b.dataPoints - a.dataPoints);
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function percentileRange(
  values: number[],
): { p25: number; p75: number } {
  if (values.length === 0) return { p25: 0, p75: 0 };
  if (values.length === 1) return { p25: values[0], p75: values[0] };

  const sorted = [...values].sort((a, b) => a - b);
  const p25Index = Math.floor(sorted.length * 0.25);
  const p75Index = Math.floor(sorted.length * 0.75);

  return {
    p25: sorted[p25Index],
    p75: sorted[p75Index],
  };
}
